import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import { getConfigurationSchedulesUrl, getScheduleConfigurationFormUrl } from 'services/navigation';
import {
  deleteScheduleConfiguration,
  getScheduleConfigurations,
  prePublishCheckScheduleConfiguration,
  publishScheduleConfiguration,
  type ScheduleConfiguration,
} from 'services/api/schedule-configuration';
import { getDefaultTablePagination, useTableSearchParams } from 'shared/UI/UIKit/Table';
import { getCurrentBellSchedule } from 'services/api';
import { useFilteredColumns } from 'common/ColumnsFilter/use-filtered-columns';
import type { BellSchedule } from 'services/api';
import type { ScheduleConfigurationPrePublishCheckDto } from 'services/api/schedule-configuration';
import type { AppTableProps, AppTableState } from 'shared/UI/UIKit/Table';
import { getColumns } from './columns';
import { mapChecksErrorsByCode } from './utils';

export const useScheduleEducationPlans = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [scheduleConfigurations, setScheduleConfigurations] = useState<ScheduleConfiguration[]>([]);
  const [total, setTotal] = useState(0);

  const [prePublishErrorsModalVisible, setPrePublishErrorsModalVisible] = useState<boolean>(false);
  const pendingPublishIdRef = useRef<number | null>(null);
  const [prePublishErrors, setPrePublishErrors] = useState<Record<
    string,
    Omit<ScheduleConfigurationPrePublishCheckDto, 'code'>[]
  > | null>(null);

  const [currentBellSchedule, setCurrentBellSchedule] = useState<BellSchedule | null>(null);

  const showScheduleConfigurationModal = () => {
    navigate(getScheduleConfigurationFormUrl());
  };

  const handleViewConfiguration = (id: number) => {
    navigate(getScheduleConfigurationFormUrl({ configurationId: String(id) }));
  };

  const handleCopyConfiguration = (id: number) => {
    navigate(getScheduleConfigurationFormUrl({ configurationId: String(id), copy: 'true' }));
  };

  const showPrePublishErrorsModal = (id: number) => {
    pendingPublishIdRef.current = id;
    setPrePublishErrorsModalVisible(true);
  };

  const hidePrePublishErrorsModal = () => {
    setPrePublishErrorsModalVisible(false);
    setPrePublishErrors(null);
  };

  const getTableItems = async (nextTableState: AppTableState) => {
    const {
      pagination: { current, pageSize },
    } = nextTableState;

    setLoading(true);

    try {
      const res = await getScheduleConfigurations({
        page: (current ?? 1) - 1,
        size: pageSize,
      });
      setScheduleConfigurations(res.content);
      setTotal(res.totalElements);
    } finally {
      setLoading(false);
    }
  };

  const onRowHandleClick: AppTableProps<ScheduleConfiguration>['onRow'] = (data) => {
    if (data.status === 'DRAFT') {
      return {
        onClick: (e) => {
          e.stopPropagation();
        },
      };
    }
    return {
      onClick: () => {
        navigate(getConfigurationSchedulesUrl({ configurationId: data.id.toString() }));
      },
    };
  };

  const handlePublishConfiguration = async (id: number) => {
    await prePublishCheckScheduleConfiguration(id).then(async (response) => {
      if (!response.length) {
        await publishScheduleConfiguration(id)
          .then(() => {
            refreshPage();
          })
          .catch(() => {
            notification.error({ message: 'Не удалось опубликовать конфигурацию расписания' });
          });
      } else {
        setPrePublishErrors(mapChecksErrorsByCode(response));
        showPrePublishErrorsModal(id);
      }
    });
  };

  const handleConfirmPublish = async () => {
    if (pendingPublishIdRef.current === null) return;
    await publishScheduleConfiguration(pendingPublishIdRef.current)
      .then(() => {
        refreshPage();
        hidePrePublishErrorsModal();
      })
      .catch(() =>
        notification.error({ message: 'Не удалось опубликовать конфигурацию расписания' }),
      );
  };

  const handleDeleteConfiguration = (id: number) => {
    deleteScheduleConfiguration(id)
      .then(() => {
        refreshPage();
      })
      .catch(() => {
        notification.error({ message: 'Не удалось удалить конфигурацию расписания' });
      });
  };

  const initialTableState: AppTableState = {
    sorters: [],
    filters: [],
    pagination: getDefaultTablePagination(),
  };

  const columns = getColumns({
    handlePublishConfiguration,
    handleViewConfiguration,
    handleDeleteConfiguration,
    handleCopyConfiguration,
  });
  const { filteredColumns, hiddenColumns, visibleKeys, setVisibleKeys } = useFilteredColumns(
    columns,
    ['id'],
  );

  const { tableState, onTableChange, refreshPage } = useTableSearchParams(
    initialTableState,
    getTableItems,
    scheduleConfigurations ? scheduleConfigurations.length : 0,
    total,
    hiddenColumns,
  );

  useEffect(() => {
    getCurrentBellSchedule().then((res) => {
      setCurrentBellSchedule(res);
    });
  }, []);

  return {
    state: {
      dataSource: scheduleConfigurations,
      columns,
      filteredColumns,
      visibleKeys,
      loading,
      tableState,
      prePublishErrorsModalVisible,
      prePublishErrors,
      currentBellSchedule,
    },
    methods: {
      onRowHandleClick,
      onTableChange,
      showScheduleConfigurationModal,
      handlePublishConfiguration,
      hidePrePublishErrorsModal,
      handleConfirmPublish,
      setVisibleKeys,
    },
  };
};
